import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { useDolfinClient } from "@/components/DolfinProvider";
import { apiErrorMessage } from "@/lib/api-error";
import { formatCurrency, formatDate } from "@/lib/format";
import type { components } from "@dolfin/api-admin";
import { ApplySupplierCreditDialog } from "@/components/ApplySupplierCreditDialog";
import { BackLink, PageContainer, PageHeader } from "@/components/PagePrimitives";
import { InfoHint } from "@/components/InfoHint";
import { AccountingSyncBadge } from "@/components/AccountingSync";
import {
  EmptyState,
  ErrorState,
  Eyebrow,
  HeaderRow,
  LoadingState,
  TH,
  TableShell,
  Timeline,
  rowLink,
  type TimelineEntry,
} from "@/components/PageUi";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Spinner } from "@/components/ui/spinner";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Link2 } from "lucide-react";
import { cn } from "@/lib/utils";

type SupplierCreditNoteResponse = components["schemas"]["SupplierCreditNoteResponse"];
type SupplierCreditApplicationResponse = components["schemas"]["SupplierCreditApplicationResponse"];
type SupplierResponse = components["schemas"]["SupplierResponse"];
type BillResponse = components["schemas"]["BillResponse"];

export interface SupplierCreditNotePageProps {
  creditNoteId: string;
  onBack?: () => void;
  onSupplierClick?: (supplierId: string) => void;
  onBillClick?: (billId: string) => void;
  /** The client holds ap.accounting and a platform is connected: shows where the note stands in the books. */
  accountingConnected?: boolean;
}

function statusLabel(status: string | undefined): string {
  return status === "PartiallyApplied" ? "Partially applied" : (status ?? "-");
}

function StatusBadge({ status }: { status: string | undefined }) {
  const available = status === "Issued" || status === "PartiallyApplied";
  return (
    <Badge
      variant="outline"
      className={cn(
        available
          ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-600 dark:text-emerald-400"
          : "border-border bg-muted text-muted-foreground",
      )}
    >
      {statusLabel(status)}
    </Badge>
  );
}

// How a credit note reached us, in words. The enum is the API's vocabulary.
const SOURCE_LABELS: Record<string, string> = {
  Manual: "Added by hand",
  Ocr: "Read off a document",
  EInvoice: "Received over the e-invoicing network",
};

function sourceLabel(source?: string | null) {
  return source ? (SOURCE_LABELS[source] ?? source) : "-";
}

function Stat({
  label,
  value,
  accent,
  share,
}: {
  label: string;
  value: React.ReactNode;
  accent?: boolean;
  /** 0-1: draws a thin bar under the value, for "how much of the credit is used". */
  share?: number;
}) {
  return (
    <div className="rounded-xl border bg-card/60 p-4 backdrop-blur-sm">
      <Eyebrow>{label}</Eyebrow>
      <p className={cn("mt-1 text-xl font-bold tracking-tight tabular-nums", accent && "text-emerald-700 dark:text-emerald-400")}>
        {value}
      </p>
      {share !== undefined ? (
        <div className="mt-2 h-1 overflow-hidden rounded-full bg-muted" aria-hidden>
          <div
            className="h-full rounded-full bg-muted-foreground/50"
            style={{ width: `${Math.max(0, Math.min(100, Math.round(share * 100)))}%` }}
          />
        </div>
      ) : null}
    </div>
  );
}

export function SupplierCreditNotePage({
  creditNoteId,
  onBack,
  onSupplierClick,
  onBillClick,
  accountingConnected,
}: SupplierCreditNotePageProps) {
  const api = useDolfinClient();
  const [note, setNote] = useState<SupplierCreditNoteResponse | null>(null);
  const [supplier, setSupplier] = useState<SupplierResponse | null>(null);
  const [applications, setApplications] = useState<SupplierCreditApplicationResponse[]>([]);
  const [bills, setBills] = useState<BillResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [applyOpen, setApplyOpen] = useState(false);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    const { data, error: apiError } = await api.GET("/v1/supplier-credit-notes/{creditNoteId}", {
      params: { path: { creditNoteId } },
    });
    if (apiError || !data) {
      setError(apiErrorMessage(apiError, "Failed to load supplier credit note"));
      setLoading(false);
      return;
    }
    setNote(data);

    const [appsRes, billsRes, supplierRes] = await Promise.all([
      api.GET("/v1/supplier-credit-notes/{creditNoteId}/applications", { params: { path: { creditNoteId } } }),
      api.GET("/v1/bills", { params: { query: { Limit: 100 } } }),
      data.supplierId
        ? api.GET("/v1/suppliers/{id}", { params: { path: { id: data.supplierId } } })
        : Promise.resolve({ data: null }),
    ]);
    setApplications(appsRes.data ?? []);
    setBills(billsRes.data?.data ?? []);
    setSupplier((supplierRes as { data: SupplierResponse | null }).data ?? null);
    setLoading(false);
  }, [api, creditNoteId]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const billLabel = useMemo(() => {
    const map = new Map<string, string>();
    for (const b of bills) {
      if (b.id) map.set(b.id, b.invoiceNumber || b.supplierName || `Bill ${b.id.slice(0, 8)}`);
    }
    return map;
  }, [bills]);

  async function handleIssue() {
    setBusy(true);
    const { error: apiError } = await api.POST("/v1/supplier-credit-notes/{creditNoteId}/issue", {
      params: { path: { creditNoteId } },
    });
    setBusy(false);
    if (apiError) {
      toast.error(apiErrorMessage(apiError, "Failed to issue credit note"));
      return;
    }
    toast.success("Credit note issued - credit now available");
    fetchAll();
  }

  async function handleDelete() {
    setBusy(true);
    const { error: apiError } = await api.DELETE("/v1/supplier-credit-notes/{creditNoteId}", {
      params: { path: { creditNoteId } },
    });
    setBusy(false);
    if (apiError) {
      toast.error(apiErrorMessage(apiError, "Failed to delete credit note"));
      return;
    }
    toast.success("Draft deleted");
    onBack?.();
  }

  if (loading) {
    return (
      <PageContainer>
        <LoadingState label="Loading credit note..." />
      </PageContainer>
    );
  }
  if (error || !note) {
    return (
      <PageContainer>
        {onBack ? <BackLink onClick={onBack}>Back to supplier credit notes</BackLink> : null}
        <ErrorState message={error || "That credit note could not be found."} onRetry={fetchAll} />
      </PageContainer>
    );
  }

  const isDraft = note.status === "Draft";
  const isEInvoice = note.source === "EInvoice";
  const canApply = note.status === "Issued" || note.status === "PartiallyApplied";
  const amount = Number(note.amount ?? 0);
  const applied = Number(note.appliedAmount ?? 0);
  const billName = (billId?: string | null) =>
    (billId && billLabel.get(billId)) || (billId ? `Bill ${billId.slice(0, 8)}` : "a bill");

  // No audit feed for credit notes, so history is pieced together from the note and its applications.
  const history: TimelineEntry[] = [
    { id: "created", title: "Recorded", at: note.createdAt, detail: sourceLabel(note.source) },
    ...(note.issuedAt
      ? [{ id: "issued", title: "Issued - credit available", at: note.issuedAt }]
      : []),
    ...applications.flatMap((a): TimelineEntry[] => {
      const entries: TimelineEntry[] = [
        {
          id: `${a.id}-applied`,
          title: `Applied to ${billName(a.billId)}`,
          at: a.createdAt,
          detail: formatCurrency(a.amount, a.currency),
        },
      ];
      if (a.status === "Reversed") {
        entries.push({
          id: `${a.id}-reversed`,
          title: `Reversed from ${billName(a.billId)}`,
          at: a.updatedAt,
          detail: `${formatCurrency(a.amount, a.currency)} returned to available credit`,
        });
      }
      return entries;
    }),
  ].sort((x, y) => new Date(y.at ?? 0).getTime() - new Date(x.at ?? 0).getTime());

  return (
    <PageContainer>
      {onBack ? <BackLink onClick={onBack}>Back to supplier credit notes</BackLink> : null}

      <PageHeader
        eyebrow="Supplier credit note"
        title={<span className="font-mono">{note.documentNumber || "Credit note"}</span>}
        subtitle={
          <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <span className="text-base font-semibold text-foreground tabular-nums">
              {formatCurrency(note.amount, note.currency)}
            </span>
            {note.supplierId && (
              <>
                <span aria-hidden>·</span>
                {onSupplierClick ? (
                  <button
                    type="button"
                    className="text-accent-blue hover:underline"
                    onClick={() => onSupplierClick(note.supplierId!)}
                  >
                    {supplier?.name ?? "View supplier"}
                  </button>
                ) : (
                  <span>{supplier?.name}</span>
                )}
              </>
            )}
            {note.reason && (
              <>
                <span aria-hidden>·</span>
                <span>{note.reason}</span>
              </>
            )}
          </span>
        }
        actions={
          <>
            <StatusBadge status={note.status ?? undefined} />
            {isDraft && (
              <>
                <Button size="sm" disabled={busy} onClick={handleIssue}>
                  {busy && <Spinner className="size-4" />}
                  Issue
                </Button>
                {/* An e-invoiced note is the supplier's document: the API refuses delete (409 SupplierCreditNote.NotDeletableEInvoice).
                    Disabled rather than hidden so the reason is on the page. */}
                {isEInvoice ? (
                  <span className="inline-flex items-center gap-1.5">
                    <Button size="sm" variant="outline" disabled>
                      Delete
                    </Button>
                    <InfoHint label="Why this cannot be deleted">
                      This credit note arrived over an e-invoicing network, so it is the supplier's
                      document rather than yours and cannot be deleted. You can still issue it and
                      apply the credit to a bill.
                    </InfoHint>
                  </span>
                ) : (
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button size="sm" variant="outline">Delete</Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-56">
                      <p className="mb-3 text-sm">Delete this draft credit note?</p>
                      <Button variant="destructive" className="w-full" disabled={busy} onClick={handleDelete}>
                        Delete
                      </Button>
                    </PopoverContent>
                  </Popover>
                )}
              </>
            )}
            {canApply && (
              <Button size="sm" onClick={() => setApplyOpen(true)}>
                <Link2 className="size-4" /> Apply to bill
              </Button>
            )}
          </>
        }
      />

      <div className="flex flex-col gap-4">
        {isDraft ? (
          <div className="rounded-xl border border-dashed bg-card/30 px-4 py-3 text-sm text-muted-foreground">
            This is a draft. Issue it to make the credit available to apply against this supplier's bills.
          </div>
        ) : null}

        <div className="grid grid-cols-2 gap-3 @2xl:grid-cols-4">
          <Stat label="Amount" value={formatCurrency(note.amount, note.currency)} />
          <Stat
            label="Applied"
            value={formatCurrency(note.appliedAmount, note.currency)}
            share={amount > 0 ? applied / amount : undefined}
          />
          <Stat label="Available" value={formatCurrency(note.available, note.currency)} accent />
          <Stat
            label="Net / Tax"
            value={
              <span className="text-base">
                {formatCurrency(note.netAmount, note.currency)}
                <span className="text-muted-foreground"> / {formatCurrency(note.taxAmount, note.currency)}</span>
              </span>
            }
          />
        </div>

        <div className="grid gap-4 lg:grid-cols-2">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Details</CardTitle>
            </CardHeader>
            <CardContent>
              <dl className="grid grid-cols-[8rem_minmax(0,1fr)] gap-x-4 gap-y-2.5 text-sm">
                <dt className="text-muted-foreground">Currency</dt>
                <dd>{note.currency}</dd>
                <dt className="text-muted-foreground">Source</dt>
                <dd>{sourceLabel(note.source)}</dd>
                {note.taxRateSnapshot ? (
                  <>
                    <dt className="text-muted-foreground">Tax rate</dt>
                    <dd>{note.taxRateSnapshot.name}</dd>
                  </>
                ) : null}
                {note.originalBillId ? (
                  <>
                    <dt className="text-muted-foreground">Raised against</dt>
                    <dd>
                      {onBillClick ? (
                        <button
                          type="button"
                          className="text-accent-blue hover:underline"
                          onClick={() => onBillClick(note.originalBillId!)}
                        >
                          {billName(note.originalBillId)}
                        </button>
                      ) : (
                        billName(note.originalBillId)
                      )}
                    </dd>
                  </>
                ) : null}
                <dt className="text-muted-foreground">Created</dt>
                <dd className="tabular-nums">{formatDate(note.createdAt)}</dd>
                <dt className="text-muted-foreground">Issued</dt>
                <dd className="tabular-nums">{note.issuedAt ? formatDate(note.issuedAt) : "Not yet"}</dd>
                {/* Issued notes are pushed, so a draft has nothing to show here yet. */}
                {accountingConnected && !isDraft ? (
                  <>
                    <dt className="text-muted-foreground">Accounting</dt>
                    <dd>
                      <AccountingSyncBadge sync={note.accountingSync} />
                    </dd>
                  </>
                ) : null}
              </dl>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">History</CardTitle>
            </CardHeader>
            <CardContent>
              <Timeline entries={history} />
            </CardContent>
          </Card>
        </div>

        <div>
          <Eyebrow className="mb-2">Applied to</Eyebrow>
          {applications.length === 0 ? (
            <EmptyState
              icon={Link2}
              title="Not applied to any bill yet"
              action={
                canApply ? (
                  <Button size="sm" variant="outline" onClick={() => setApplyOpen(true)}>
                    Apply to bill
                  </Button>
                ) : undefined
              }
            >
              {isDraft
                ? "Issue the credit note first, then net it against a bill you owe this supplier."
                : "Net the available credit against a bill you owe this supplier."}
            </EmptyState>
          ) : (
            <TableShell>
              <Table>
                <TableHeader>
                  <HeaderRow>
                    <TableHead className={TH}>Bill</TableHead>
                    <TableHead className={cn(TH, "text-right")}>Amount</TableHead>
                    <TableHead className={cn(TH, "text-right")}>Applied</TableHead>
                    <TableHead className={cn(TH, "text-right")}>Status</TableHead>
                  </HeaderRow>
                </TableHeader>
                <TableBody>
                  {applications.map((a) => {
                    const reversed = a.status === "Reversed";
                    return (
                      <TableRow
                        key={a.id}
                        {...rowLink(onBillClick && a.billId ? () => onBillClick(a.billId!) : undefined)}
                      >
                        <TableCell className="text-sm font-semibold">{billName(a.billId)}</TableCell>
                        <TableCell
                          className={cn(
                            "text-right text-sm tabular-nums",
                            reversed && "text-muted-foreground line-through",
                          )}
                        >
                          {formatCurrency(a.amount, a.currency)}
                        </TableCell>
                        <TableCell className="text-right text-sm text-muted-foreground tabular-nums">
                          {formatDate(a.createdAt)}
                        </TableCell>
                        <TableCell className="text-right">
                          <Badge
                            variant="outline"
                            className={cn(
                              reversed
                                ? "border-border bg-muted text-muted-foreground"
                                : "border-emerald-500/40 bg-emerald-500/10 text-emerald-600 dark:text-emerald-400",
                            )}
                          >
                            {a.status}
                          </Badge>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableShell>
          )}
        </div>
      </div>

      <ApplySupplierCreditDialog
        open={applyOpen}
        onOpenChange={setApplyOpen}
        creditNote={note}
        onApplied={fetchAll}
      />
    </PageContainer>
  );
}
